import React, { memo, useEffect, useMemo } from 'react';
import { useParams, useLocation, useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { validators } from 'investira.sdk';
import { Button, Icon, Stack, Typography, IconButton, Tooltip } from 'investira.react.components';
import { ReportsTemplate } from '../../components/templates';
import { useRelatorioContext } from '../../contexts/RelatorioContext';
import { OUTPUT } from '../../enums';
import ReportParamsDialog from './_components/ReportParamsDialog';
import withOutput from '../../hoc/withOutput';
import useAcesso from '../../hooks/useAcesso';
import usePerfil from '../../hooks/usePerfil';
import columns from './columns';

const ReportsOutputsCache = memo(props => {
  const params = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { podeEditarGerenciarAprovar } = useAcesso();
  const { usuarioPrioridade } = usePerfil();

  const OUTPUT_ID = OUTPUT[params.output]?.output_id;

  const { actions } = useRelatorioContext();
  const carteiras = useSelector(store => store.carteiras.data);
  const portfolios = useSelector(store => store.portfolios.data);
  const xColumns = useMemo(() => columns[params.output], [params.output]);

  const renderNew = pAcesso => {
    if (!pAcesso) {
      return null;
    }

    return (
      <Button
        disabled={validators.isEmpty(carteiras)}
        variant="contained"
        color="primary"
        startIcon={<Icon iconName="insert" size={16} />}
        size="small"
        sx={{ px: 2 }}
        onClick={actions.handleNewConsult}>
        Novo Relatório
      </Button>
    );
  };

  const renderSettings = pAcesso => {
    if (!pAcesso) {
      return null;
    }

    if (usuarioPrioridade > 4) {
      return null;
    }

    if (['duration', 'var', 'conferenciacustodiante'].includes(params.output)) {
      return null;
    }

    return (
      <Stack flexGrow={0}>
        <Tooltip title={`Configurar ${OUTPUT[params.output].descricao}`}>
          <IconButton
            disabled={validators.isEmpty(carteiras)}
            color="primary"
            size="small"
            onClick={() => navigate(`${location.pathname}/configuracoes`)}>
            <Icon iconName="gear" size={24} />
          </IconButton>
        </Tooltip>
      </Stack>
    );
  };

  useEffect(() => {
    actions.handleOutputUpdate(params.output);
  }, [params.output]);

  return (
    <>
      <ReportsTemplate
        key={params.output}
        subtitle="Relatórios"
        title={OUTPUT[params.output].descricao}
        headerRenderActions={
          <Stack direction="row" spacing={2}>
            {validators.isEmpty(carteiras) ? (
              <>
                <Stack direction="row" spacing={2} alignItems="center">
                  <Icon iconName="warning" color="warn" size={21} />

                  <Typography color="textPrimary" variant="caption">
                    Necessário possuir uma carteira favorita
                  </Typography>
                </Stack>
                <Button
                  variant="contained"
                  color="primary"
                  startIcon={<Icon iconName="wallet" size={16} />}
                  size="small"
                  sx={{ px: 2 }}
                  onClick={() => navigate('/carteiras')}>
                  Ir para Carteiras
                </Button>
              </>
            ) : (
              <>
                {renderNew(podeEditarGerenciarAprovar(OUTPUT_ID))}
                {renderSettings(podeEditarGerenciarAprovar(OUTPUT_ID))}
              </>
            )}
          </Stack>
        }
        dataGridProps={{
          output: OUTPUT[params.output],
          columns: xColumns,
          data: props.caches,
          readData: props.readCacheList,
          isLoading: props.isLoading,
          initialState: {
            pagination: props.pagination
            //columnVisibility: { carteira: false },
          },
          //manualFiltering: true,
          //manualSorting: true,
          manualPagination: true,
          getRowId: row => row.log_id,
          state: {
            isLoading: props.isLoading,
            pagination: props.pagination,
            showProgressBars: props.isRefetching,
            //columnFilters: props.columnFilters,
            //sorting: props.sorting,
            rowSelection: props.rowSelection
          },
          rowCount: props.rowCount,
          //onColumnFiltersChange: props.setColumnFilters,
          //onSortingChange: props.setSorting,
          onPaginationChange: props.setPagination,
          onRowSelectionChange: props.setRowSelection,
          //onGlobalFilterChange: props.setGlobalFilter,
          onBulkDeleteClick: props.bulkDeleteCaches
        }}
      />
      <ReportParamsDialog
        carteiras={carteiras}
        portfolios={portfolios}
        output={params.output}
        lastOutput={props.caches && props.caches.length > 0 ? props.caches[0] : []}
      />
    </>
  );
});

ReportsOutputsCache.displayName = 'ReportsOutputsCache';

export default withOutput(ReportsOutputsCache);
